import { MAX_FOOTPRINT_SHAPES_PER_LEVEL } from '@alpinehomeair/common';
import { FootprintShapeType, PositionType } from 'data/types';
import {
  FootprintRotationDirection,
  FootprintStrokeResult
} from 'utils/footprint-extend-types';
import {
  footprintBoundingBox,
  footprintBoundsCenter
} from 'utils/footprint-polygons';
import {
  footprintBboxCenter,
  rotateFootprintPoints
} from 'utils/footprint-shapes-geometry';
import {
  footprintShapesToPoints,
  isValidFootprintShape,
  unionFootprintShapes
} from 'utils/footprint-union';
import {
  freehandToFootprintShape,
  MIN_DRAWN_AREA_SQFT
} from 'utils/freehand-outline';
import { polygonAreaAbs } from 'utils/space-gap-fill';

const MIN_EXTENSION_AREA_SQFT = 0.01;
const MIN_EXTENSION_SHAPE_AREA_SQFT = 2;

export function rotateFootprintOutline(
  outline: PositionType[],
  direction: FootprintRotationDirection
): PositionType[] {
  return rotateFootprintPoints(
    outline,
    direction === 'left' ? 270 : 90,
    footprintBboxCenter(outline)
  );
}

export function rotateFootprintShapes(
  shapes: FootprintShapeType[],
  direction: FootprintRotationDirection
): FootprintShapeType[] {
  const bounds = footprintBoundingBox(footprintShapesToPoints(shapes));
  if (!bounds) return shapes;
  const pivot = footprintBoundsCenter(bounds);
  return shapes.map((shape) => ({
    ...shape,
    points: rotateFootprintPoints(
      shape.points,
      direction === 'left' ? 270 : 90,
      pivot
    )
  }));
}

function shapesArea(shapes: FootprintShapeType[]): number {
  return shapes.reduce((sum, shape) => sum + polygonAreaAbs(shape.points), 0);
}

function touchesStroke(shape: FootprintShapeType, stroke: PositionType[]) {
  return !unionFootprintShapes([shape.points, stroke]).isDisjoint;
}

function mergeShapesWithStroke(
  shapes: FootprintShapeType[],
  touching: Set<number>,
  stroke: PositionType[]
): FootprintShapeType[] {
  const touchingPoints = shapes
    .filter((_, index) => touching.has(index))
    .map((shape) => shape.points);
  const merged = unionFootprintShapes([...touchingPoints, stroke]).outlines.map(
    (outline) => ({ points: outline, vertexEdited: true })
  );
  const firstTouching = Math.min(...Array.from(touching));
  const next: FootprintShapeType[] = [];
  shapes.forEach((shape, index) => {
    if (index === firstTouching) {
      next.push(...merged);
    } else if (!touching.has(index)) {
      next.push(shape);
    }
  });
  return next;
}

export function extendFootprintWithStroke(
  currentShapes: FootprintShapeType[],
  rawStroke: PositionType[]
): FootprintStrokeResult {
  const shapes = currentShapes.filter((shape) =>
    isValidFootprintShape(shape.points)
  );
  const stroke = freehandToFootprintShape(
    rawStroke,
    shapes.length > 0 ? MIN_EXTENSION_SHAPE_AREA_SQFT : MIN_DRAWN_AREA_SQFT
  );
  if (!stroke) return { status: 'invalid-stroke' };

  const newShape: FootprintShapeType = { points: stroke, vertexEdited: true };
  if (shapes.length === 0) {
    return { status: 'applied', shapes: [newShape] };
  }

  const touching = new Set<number>();
  shapes.forEach((shape, index) => {
    if (touchesStroke(shape, stroke)) touching.add(index);
  });

  if (touching.size === 0) {
    if (shapes.length >= MAX_FOOTPRINT_SHAPES_PER_LEVEL) {
      return { status: 'limit-reached' };
    }
    return { status: 'applied', shapes: [...shapes, newShape] };
  }

  const merged = mergeShapesWithStroke(shapes, touching, stroke);
  const addedArea = shapesArea(merged) - shapesArea(shapes);
  if (addedArea < MIN_EXTENSION_AREA_SQFT) return { status: 'unchanged' };

  return { status: 'applied', shapes: merged };
}
